// Control Room render core — ONE implementation of the business-picture
// visuals, consumed by BOTH the hosted React cockpit and the Foundation
// pack's generated member reports (the eng-review DRY mandate, executed).
//
// Rules of this module:
// - Dependency-free, build-free: pure functions, (data) → HTML/SVG string.
// - No model in the render path: deterministic from the data it is handed.
// - Themed ONLY via --cr-* CSS variables (neutral editorial fallbacks baked
//   in); the cockpit maps them to Foundry tokens, the member report maps
//   them to the member's detected brand. The data carries no styling.
// - Every dynamic string is escaped. Interactivity hooks are data-* attrs;
//   consumers may wire them (React) or leave the output as a document (pack).
// - Empty states are designed, not blank: a day-1 member report must look
//   intentional and point at the next move.
//
// Canonical copy: ai-brand-lab `shared/control-room-render/render-core.mjs`.
// The pack vendors this file at `foundation/runtime/render-core.mjs` — edit
// here, then re-copy there (keep the two byte-identical).

export function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

const esc = escapeHtml;

/** Baseline CSS for the cr- classes. Include once per document/scope. */
export function baseStyles() {
  return `
.cr-module{background:var(--cr-surface,#fff);border:1px solid var(--cr-line,#e8e3db);border-radius:12px;padding:10px}
.cr-module svg{display:block;width:100%}
.cr-note{margin:6px 8px 2px;font-size:10.5px;line-height:1.5;color:var(--cr-quiet,#8b847a);font-family:ui-monospace,SFMono-Regular,Menlo,monospace}
.cr-empty{padding:26px 18px;text-align:center;font-size:12.5px;color:var(--cr-quiet,#8b847a);font-family:ui-monospace,SFMono-Regular,Menlo,monospace}
.cr-empty b{display:block;margin-bottom:4px;color:var(--cr-text,#201e1b);font-weight:650}
.cr-ladder{display:flex;gap:8px;overflow-x:auto;padding:4px 2px 6px;min-width:0}
.cr-ladder-step{flex:1 1 0;min-width:124px;border-radius:10px;border:1px solid var(--cr-line,#e8e3db);padding:10px 11px 9px;background:var(--cr-surface,#fff)}
.cr-ladder-step[data-state="done"],.cr-ladder-step[data-state="active"]{border-color:var(--cr-text,#201e1b)}
.cr-ladder-step[data-state="climb"]{border-style:dashed;opacity:.85}
.cr-ladder-step[data-state="before"]{background:var(--cr-surface-2,#f4f1ea)}
.cr-ladder-code{display:inline-block;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:10px;font-weight:700;padding:2px 7px;border-radius:5px;border:1.5px solid var(--cr-text,#201e1b);background:var(--cr-primary,#e8e3db);color:var(--cr-on-primary,#201e1b)}
.cr-ladder-step[data-state="climb"] .cr-ladder-code,.cr-ladder-step[data-state="before"] .cr-ladder-code{background:transparent;border-style:dashed;border-color:var(--cr-line-strong,#d7d1c6);color:var(--cr-quiet,#8b847a)}
.cr-ladder-tag{margin-left:6px;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:8.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--cr-accent,#5e594f)}
.cr-ladder-name{margin-top:5px;font-size:12.5px;font-weight:650;line-height:1.25;color:var(--cr-text,#201e1b)}
.cr-ladder-step[data-state="before"] .cr-ladder-name{color:var(--cr-muted,#5e594f)}
.cr-ladder-desc{margin-top:3px;font-size:10.5px;line-height:1.45;color:var(--cr-quiet,#8b847a)}
`.trim();
}

// ── The Ladder (L0 → L4 climb, with self-location) ─────────────────────

/**
 * steps: [{ code, name, desc, state: 'before'|'done'|'active'|'climb', tag? }]
 * The ascending offset is the promise made visible.
 */
export function renderLadder(steps = []) {
  if (!steps.length) return '';
  const n = steps.length;
  const items = steps
    .map((s, i) => {
      const lift = (n - 1 - i) * 8;
      return `<div class="cr-ladder-step" data-state="${esc(s.state)}" style="margin-top:${lift}px">
<span class="cr-ladder-code">${esc(s.code)}</span>${s.tag ? `<span class="cr-ladder-tag">${esc(s.tag)}</span>` : ''}
<div class="cr-ladder-name">${esc(s.name)}</div>
<div class="cr-ladder-desc">${esc(s.desc)}</div>
</div>`;
    })
    .join('');
  return `<div class="cr-ladder">${items}</div>`;
}

// ── The System Map (the business as a transit map) ─────────────────────

const MAP = { x0: 252, gap: 104, row: 64, top: 34 };

/**
 * data: {
 *   brain: { title, lines: [string] },
 *   routes: [{ id, name, featured?, steps: [{ ix, name, ref?, hub?, terminal?, note?, state? }] }],
 * }
 * opts: { highlight?: ref }   // consumers re-render with a ref to trace it
 * Station state: 'done' (filled) · 'active' (ringed) · default (hollow); hub = double ring.
 */
export function renderSystemMap(data, opts = {}) {
  const routes = data?.routes ?? [];
  if (!routes.length) {
    return `<div class="cr-module"><div class="cr-empty"><b>Your map draws itself as you build.</b>Finish your workspace setup and the first routes appear here — every one reading your brain.</div></div>`;
  }
  const { x0, gap, row, top } = MAP;
  const maxSteps = Math.max(...routes.map((r) => r.steps.length));
  const width = x0 + (maxSteps - 1) * gap + 120;
  const height = top + routes.length * row + 12;
  const brainCy = top + ((routes.length - 1) * row) / 2 + 20;
  const hl = opts.highlight ?? null;

  let svg = '';
  // brain feed lines
  for (let i = 0; i < routes.length; i++) {
    const y = top + i * row + 20;
    svg += `<path d="M 96 ${brainCy} C 130 ${brainCy}, 150 ${y}, ${x0 - 26} ${y}" fill="none" stroke="var(--cr-accent,#5e8a8a)" stroke-opacity="${hl ? 0.12 : 0.3}" stroke-width="1.4"/>`;
  }
  // brain plaque
  const brainTitle = esc(data?.brain?.title ?? 'THE BRAIN');
  const brainLines = (data?.brain?.lines ?? []).slice(0, 2);
  svg += `<g><rect x="6" y="${brainCy - 25}" width="92" height="50" rx="10" fill="var(--cr-primary-soft,#f4f1ea)" stroke="var(--cr-text,#201e1b)" stroke-width="1.5"/>
<text x="52" y="${brainCy - 5}" text-anchor="middle" font-family="ui-monospace,Menlo,monospace" font-size="10.5" font-weight="700" fill="var(--cr-text,#201e1b)">${brainTitle}</text>`;
  brainLines.forEach((line, i) => {
    svg += `<text x="52" y="${brainCy + 8 + i * 10}" text-anchor="middle" font-family="ui-monospace,Menlo,monospace" font-size="7.2" letter-spacing="0.4" fill="var(--cr-muted,#5e594f)">${esc(line)}</text>`;
  });
  svg += `</g>`;

  routes.forEach((r, i) => {
    const y = top + i * row + 20;
    const xEnd = x0 + (r.steps.length - 1) * gap;
    svg += `<g><text x="${x0 - 14}" y="${y - 14}" text-anchor="end" font-family="ui-monospace,Menlo,monospace" font-size="10" font-weight="600" fill="${r.featured ? 'var(--cr-text,#201e1b)' : 'var(--cr-muted,#5e594f)'}" data-route="${esc(r.id)}" style="cursor:pointer">${r.featured ? '★ ' : ''}${esc(r.name)}</text>`;
    svg += `<line x1="${x0 - 26}" y1="${y}" x2="${xEnd}" y2="${y}" stroke="var(--cr-line-strong,#d7d1c6)" stroke-width="2.5"/>`;
    r.steps.forEach((s, j) => {
      const x = x0 + j * gap;
      const isHl = hl !== null && s.ref === hl;
      const dim = hl !== null && s.ref !== hl;
      const ink = 'var(--cr-text,#201e1b)';
      const fillFor = s.state === 'done' ? 'var(--cr-primary,#9a958c)' : isHl ? 'var(--cr-accent,#5e8a8a)' : 'var(--cr-surface,#fff)';
      const title = s.terminal
        ? `${s.name} — hand-off`
        : `${s.name}${s.hub ? ' — interchange: shared across routes' : ''}${s.state === 'done' ? ' · done' : s.state === 'active' ? ' · up next' : ''}${s.note ? ` · ${s.note}` : ''}`;
      svg += `<g data-ref="${esc(s.ref ?? '')}" opacity="${dim ? 0.32 : 1}" style="cursor:${s.ref ? 'pointer' : 'default'}"><title>${esc(title)}</title>`;
      if (s.terminal) {
        svg += `<rect x="${x - 5}" y="${y - 5}" width="10" height="10" rx="2" fill="var(--cr-surface,#fff)" stroke="var(--cr-quiet,#8b847a)" stroke-width="1.5" stroke-dasharray="3 2"/>`;
      } else if (s.hub) {
        svg += `<circle cx="${x}" cy="${y}" r="9" fill="none" stroke="${isHl ? 'var(--cr-accent,#5e8a8a)' : ink}" stroke-width="1.5"/><circle cx="${x}" cy="${y}" r="5" fill="${isHl ? 'var(--cr-accent,#5e8a8a)' : 'var(--cr-primary,#e8c95a)'}" stroke="${ink}" stroke-width="1.5"/>`;
      } else if (s.state === 'active') {
        svg += `<circle cx="${x}" cy="${y}" r="7.5" fill="none" stroke="var(--cr-accent,#5e8a8a)" stroke-width="1.8"/><circle cx="${x}" cy="${y}" r="4" fill="var(--cr-surface,#fff)" stroke="${ink}" stroke-width="1.5"/>`;
      } else {
        svg += `<circle cx="${x}" cy="${y}" r="5" fill="${fillFor}" stroke="${ink}" stroke-width="1.5"/>`;
      }
      svg += `<text transform="rotate(26 ${x + 6} ${y + 16})" x="${x + 6}" y="${y + 16}" font-family="ui-monospace,Menlo,monospace" font-size="9.5" fill="${isHl ? 'var(--cr-accent,#5e8a8a)' : s.terminal ? 'var(--cr-quiet,#8b847a)' : 'var(--cr-muted,#5e594f)'}">${esc(s.name)}</text></g>`;
    });
    svg += `</g>`;
  });

  return `<div class="cr-module"><svg viewBox="0 0 ${width} ${height}" style="min-width:${Math.round(width * 0.78)}px" role="img" aria-label="System map: every route reads the brain">${svg}</svg><p class="cr-note">Every route reads the brain · double-ring stations are interchanges · hover a station to trace it</p></div>`;
}

// ── The Loop Pulse (the business improving over time) ──────────────────

const DAY = 86_400_000;

/**
 * data: {
 *   lanes: [{ key, label }],
 *   events: [{ date: 'YYYY-MM-DD', lane, label, bad? }],
 *   emptyTitle?, emptyNote?,
 * }
 */
export function renderLoopPulse(data) {
  const lanes = data?.lanes ?? [];
  const events = (data?.events ?? []).filter((e) => /^\d{4}-\d{2}-\d{2}$/.test(e?.date ?? ''));
  if (!events.length) {
    return `<div class="cr-module"><div class="cr-empty"><b>${esc(data?.emptyTitle ?? 'Your loop draws its first marks here.')}</b>${esc(data?.emptyNote ?? 'Every run, experiment and approval lands on this strip — the picture of your business improving.')}</div></div>`;
  }
  const ts = events.map((e) => new Date(e.date + 'T00:00:00Z').getTime());
  const min = Math.min(...ts) - DAY;
  const max = Math.max(...ts) + DAY;
  const W = 860;
  const LX = 96;
  const x = (d) => LX + ((new Date(d + 'T00:00:00Z').getTime() - min) / (max - min)) * (W - LX - 16);
  const laneY = (k) => 22 + lanes.findIndex((l) => l.key === k) * 26;
  const H = 22 + lanes.length * 26 + 18;

  let svg = '';
  for (let t = min + DAY; t <= max; t += DAY * 7) {
    const tx = LX + ((t - min) / (max - min)) * (W - LX - 16);
    svg += `<line x1="${tx}" y1="12" x2="${tx}" y2="${H - 22}" stroke="var(--cr-line,#e8e3db)" stroke-width="1" stroke-dasharray="2 4"/><text x="${tx}" y="${H - 8}" text-anchor="middle" font-family="ui-monospace,Menlo,monospace" font-size="8.5" fill="var(--cr-quiet,#8b847a)">${new Date(t).toISOString().slice(5, 10)}</text>`;
  }
  for (const l of lanes) {
    const count = events.filter((e) => e.lane === l.key).length;
    svg += `<text x="8" y="${laneY(l.key) + 3.5}" font-family="ui-monospace,Menlo,monospace" font-size="9.5" letter-spacing="1" fill="var(--cr-quiet,#8b847a)">${esc(l.label.toUpperCase())} <tspan fill="var(--cr-text,#201e1b)" font-weight="700">${count}</tspan></text><line x1="${LX}" y1="${laneY(l.key)}" x2="${W - 16}" y2="${laneY(l.key)}" stroke="var(--cr-line,#e8e3db)" stroke-width="1.5"/>`;
  }
  const ink = 'var(--cr-text,#201e1b)';
  for (const e of events) {
    const ex = x(e.date);
    const ey = laneY(e.lane);
    if (ey < 22) continue; // unknown lane — skip rather than mis-plot
    const t = `<title>${esc(`${e.date} · ${e.label}`)}</title>`;
    const li = lanes.findIndex((l) => l.key === e.lane) % 4;
    if (li === 0) svg += `<g>${t}<circle cx="${ex}" cy="${ey}" r="4.5" fill="var(--cr-accent,#5e8a8a)" stroke="${ink}" stroke-width="1"/></g>`;
    else if (li === 1) svg += `<g>${t}<rect x="${ex - 4.5}" y="${ey - 4.5}" width="9" height="9" rx="1.5" fill="${e.bad ? 'var(--cr-bad,#b4431f)' : 'var(--cr-primary,#e8c95a)'}" stroke="${ink}" stroke-width="1.2"/></g>`;
    else if (li === 2) svg += `<g>${t}<path d="M ${ex} ${ey - 6} L ${ex + 6} ${ey} L ${ex} ${ey + 6} L ${ex - 6} ${ey} Z" fill="var(--cr-primary,#e8c95a)" stroke="${ink}" stroke-width="1.3"/></g>`;
    else svg += `<g>${t}<circle cx="${ex}" cy="${ey}" r="4.5" fill="var(--cr-surface,#fff)" stroke="${e.bad ? 'var(--cr-bad,#b4431f)' : 'var(--cr-accent,#5e8a8a)'}" stroke-width="1.8"/></g>`;
  }
  return `<div class="cr-module"><svg viewBox="0 0 ${W} ${H}" role="img" aria-label="Loop activity over time">${svg}</svg><p class="cr-note">drawn straight from your own records — every mark is something that actually happened</p></div>`;
}
